import { DefaultValue, atom, selector } from 'recoil';
import { assoc, defaultTo, pick } from 'ramda';
import type { Filter } from './constants';
import { parseFilter } from './utils/parseFilter';

const FILTER_KEY = 'app.components.Filter' as const;

const parsedSearch = parseFilter(window.location.search);

const loadedFromUrlState = atom({
  key: `${FILTER_KEY}.loadedFromUrl`,
  default: false,
  effects: [
    ({ setSelf }) => {
      if (parsedSearch.filter) setSelf(true);
    },
  ],
});

interface ValuesAndDefaults {
  values: Filter;
  defaultValues: Partial<Filter>;
}

const filterValuesAndRequiredState = atom<ValuesAndDefaults>({
  key: `${FILTER_KEY}.valuesAndDefaultsState`,
  default: {
    values: {},
    defaultValues: {},
  },
  effects: [
    ({ setSelf }) => {
      if (parsedSearch.filter)
        setSelf(prev => (prev instanceof DefaultValue ? prev : { ...prev, values: parsedSearch.filter ?? {} }));
    },
    ({ onSet }) => {
      onSet((newState, prevState) => {});
    },
  ],
});

const filterValuesAndDefaultsSelector = selector<ValuesAndDefaults>({
  key: `${FILTER_KEY}.valuesAndDefaultsSelector`,
  get: ({ get }) => get(filterValuesAndRequiredState),
  set: ({ set, reset }, newValue) => {
    if (newValue instanceof DefaultValue) {
      reset(filterValuesAndRequiredState);
      return;
    }

    set(filterValuesAndRequiredState, {
      defaultValues: newValue.defaultValues,
      values: Object.entries(newValue.defaultValues).reduce((acc, [key, value]) => {
        return assoc(key, defaultTo(value, acc[key as keyof Filter]), acc);
      }, newValue.values),
    });
  },
});

export const pickedFieldsState = atom<ReadonlyArray<keyof Filter>>({
  key: `${FILTER_KEY}.pickedFields`,
  default: [],
});

/**
 * @internal This is an internal selector and should not be used outside of the custom hooks.
 */
export const filterState = selector<Filter>({
  key: `${FILTER_KEY}.filter`,
  get: ({ get }) => get(filterValuesAndDefaultsSelector).values,
  set: ({ set, reset }, newValue) => {
    if (newValue instanceof DefaultValue) {
      reset(filterValuesAndDefaultsSelector);
      return;
    }
    set(filterValuesAndDefaultsSelector, prev => ({ ...prev, values: newValue }));
  },
});

/**
 * @internal This is an internal selector and should not be used outside of the custom hooks.
 */
export const pickedFilterSelector = selector<Partial<Filter>>({
  key: `${FILTER_KEY}.pickedFilter`,
  get: ({ get }) => {
    const pickedFields = get(pickedFieldsState);
    const filter = get(filterValuesAndDefaultsSelector).values;

    return pick(pickedFields, filter);
  },
});

/**
 * @internal This is an internal selector and should not be used outside of the custom hooks.
 */
export const filterConfigSelector = selector<
  ValuesAndDefaults & {
    /**
     * Indicates if the filter state was set from the URL.
     */
    loadedFromUrl: boolean;
    pickedFields: ReadonlyArray<keyof Filter>;
  }
>({
  key: `${FILTER_KEY}.filterWithMeta`,
  get: ({ get }) => ({
    ...get(filterValuesAndDefaultsSelector),
    loadedFromUrl: get(loadedFromUrlState),
    pickedFields: get(pickedFieldsState),
  }),
  set: ({ set, reset }, newConfig) => {
    if (newConfig instanceof DefaultValue) {
      reset(filterValuesAndDefaultsSelector);
      reset(pickedFieldsState);
      reset(loadedFromUrlState);
      return;
    }
    const { values, defaultValues, pickedFields, loadedFromUrl } = newConfig;
    set(filterValuesAndDefaultsSelector, { defaultValues, values });
    set(pickedFieldsState, pickedFields);
    set(loadedFromUrlState, loadedFromUrl);
  },
});
